import { CheckIcon, MinusIcon, PlusIcon } from '@heroicons/react/20/solid';
import { cs } from '@bbkAdminUtils/css-utils';
import Button from '@bbkPricingPage/components/button/pricing-card-button';
import RenderAlignedHtml from '@bbkPricingPage/components/card/components/aligned-html-render';
import { PriceComponent } from '@bbkPricingPage/components/card/components/price-component';
import { Switch } from '@bbkPricingPage/components/switch/switch';
import {
  PricingModel,
  type Frequency,
  type Price,
} from '@bbkPricingPage/types/pricing-table-types';

export const AddonItem = ({
  name,
  description,
  addonPrice,
  isToggleEnabled,
  isMandatory,
  frequency,
  quantity,
  onToggle,
  onQuantityChange,
  selectedPlanId,
}: {
  name: string;
  description?: string;
  addonPrice: Price;
  isToggleEnabled: boolean;
  isMandatory: boolean;
  frequency: Frequency;
  quantity?: number;
  onToggle: () => void;
  onQuantityChange: (quantity: number) => void;
  selectedPlanId: string;
}) => {
  const showQuantitySwitch = addonPrice.pricing_model !== PricingModel.flat_fee;
  const effectiveQuantity = showQuantitySwitch ? (quantity ?? 1) : 1;

  return (
    <div className="tw-pt-2">
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
        {/* Left: Name + Description — flex-1 so it fills space without bleeding past switch */}
        <div className="tw-flex tw-flex-1 tw-flex-col">
          <h5
            className={cs(
              'tw-text-sm tw-font-semibold',
              '!tw-text-[var(--pricing-page-text-font-text-color)]'
            )}
          >
            {name}
          </h5>
          {description && (
            <span className="tw-text-sm tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_60%,transparent)]">
              <RenderAlignedHtml value={description} />
            </span>
          )}
          {(isToggleEnabled || isMandatory) && showQuantitySwitch && (
            <div className="tw-mt-2">
              {isMandatory ? (
                <span className="tw-text-l tw-font-medium tw-text-[var(--pricing-page-text-font-text-color)]">
                  {effectiveQuantity}
                  <span className="tw-text-sm tw-text-[var(--pricing-page-text-font-text-color)]">
                    {effectiveQuantity === 1 ? ' Unit' : ' Units'}
                  </span>
                </span>
              ) : (
                <div className="tw-flex tw-items-center tw-space-x-4">
                  <Button
                    id={`decrease-${addonPrice.billing_system_id}`}
                    className="tw-pl-0"
                    variant="plain"
                    onClick={() =>
                      onQuantityChange(Math.max(1, effectiveQuantity - 1))
                    }
                    trackId={`addon-decrease-${addonPrice.billing_system_id}`}
                    trackPayload={{
                      addonId: addonPrice.billing_system_id,
                      currentQuantity: effectiveQuantity,
                    }}
                  >
                    <MinusIcon className="tw-h-4 tw-w-4" />
                  </Button>
                  <span className="tw-font-medium">{effectiveQuantity}</span>
                  <Button
                    id={`increase-${addonPrice.billing_system_id}`}
                    variant="plain"
                    onClick={() => onQuantityChange(effectiveQuantity + 1)}
                    trackId={`addon-increase-${addonPrice.billing_system_id}`}
                    trackPayload={{
                      addonId: addonPrice.billing_system_id,
                      currentQuantity: effectiveQuantity,
                    }}
                  >
                    <PlusIcon className="tw-h-4 tw-w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Right: Switch or Included — shrink-0 so it never gets pushed */}
        <div className="tw-shrink-0">
          {isMandatory ? (
            <span className="tw-inline-flex tw-items-center tw-rounded-full tw-font-medium tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_60%,transparent)]">
              <CheckIcon className="tw-mr-1 tw-h-5 tw-w-5 tw-text-[var(--pricingtable-style-accent-color)]" />
              Included
            </span>
          ) : (
            <Switch
              id={`toggle-${addonPrice.billing_system_id}`}
              enabled={isToggleEnabled}
              onToggle={onToggle}
            />
          )}
        </div>
      </div>
      {/* Price row — independent of description height */}
      {(isToggleEnabled || isMandatory) && (
        <div className="tw-flex tw-items-center tw-justify-end">
          <div className="tw-text-right">
            <span className="tw-font-medium">
              <PriceComponent
                id={addonPrice.billing_system_id}
                price={addonPrice}
                quantity={showQuantitySwitch ? effectiveQuantity : 1}
                frequency={frequency}
                frequencyOrientation="row"
                className="tw-items-center tw-justify-end tw-gap-1"
                priceClassName={
                  showQuantitySwitch
                    ? 'tw-text-right tw-font-semibold tw-text-base'
                    : 'tw-text-right tw-text-base'
                }
                suffixClassName="tw-text-right tw-font-normal tw-text-sm tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_60%,transparent)]"
                selectedPlanId={selectedPlanId}
                ignoreEqualHeight={true}
              />
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
